import React, { useState, useEffect } from 'react';
import { XMarkIcon, CheckIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { PHONE_PLACEHOLDER_MASK } from '../../config/region';
import { Lead, ValidationError } from './newRunTypes';

interface ErrorEditorModalProps {
  leads: Lead[];
  validationErrors: ValidationError[];
  onSaveCorrections: (correctedLeads: Lead[]) => void;
  onClose: () => void;
}

interface EditableLead extends Lead {
  originalRow: number;
  errors: string[];
}

const ErrorEditorModal: React.FC<ErrorEditorModalProps> = ({
  leads,
  validationErrors,
  onSaveCorrections,
  onClose,
}) => {
  const [initialErrorCount] = useState(validationErrors.length);
  const [editableLeads, setEditableLeads] = useState<EditableLead[]>(() => {
    const errorRowNumbers = new Set(validationErrors.map(e => e.row));
    return leads
      .map((lead, index) => ({
        ...lead,
        originalRow: index + 2,
        errors: validationErrors
          .filter(e => e.row === index + 2)
          .map(e => e.field === 'general' ? e.message : `${e.field}: ${e.message}`),
      }))
      .filter(lead => errorRowNumbers.has(lead.originalRow));
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(editableLeads.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentPageLeads = editableLeads.slice(startIndex, endIndex);

  const handleFieldChange = (index: number, field: keyof Lead, value: string) => {
    const updated = [...editableLeads];
    updated[index] = { ...updated[index], [field]: value };
    setEditableLeads(updated);
  };

  const handleSave = () => {
    const correctedLeads = [...leads];
    editableLeads.forEach(editedLead => {
      const arrayIndex = editedLead.originalRow - 2;
      if (arrayIndex >= 0 && arrayIndex < correctedLeads.length) {
        correctedLeads[arrayIndex] = {
          name: editedLead.name,
          phone: editedLead.phone,
          email: editedLead.email,
        };
      }
    });
    
    onSaveCorrections(correctedLeads);
  };

  // Track when validation errors change after save
  useEffect(() => {
    const currentErrorCount = validationErrors.length;
    
    // If all errors are fixed, show success and close modal
    if (currentErrorCount === 0 && initialErrorCount > 0) {
      setSuccessMessage(`All errors fixed successfully!`);
      setTimeout(() => {
        setSuccessMessage(null);
        onClose();
      }, 2000);
      return;
    }
    
    // If some errors were fixed but more remain
    if (currentErrorCount < initialErrorCount && currentErrorCount > 0) {
      const fixedCount = initialErrorCount - currentErrorCount;
      setSuccessMessage(`${fixedCount} ${fixedCount === 1 ? 'error' : 'errors'} fixed successfully!`);
      setTimeout(() => setSuccessMessage(null), 4000);
      
      // Update editable leads with new error state
      const errorRowNumbers = new Set(validationErrors.map(e => e.row));
      const updated = leads
        .map((lead, index) => ({
          ...lead,
          originalRow: index + 2,
          errors: validationErrors
            .filter(e => e.row === index + 2)
            .map(e => e.field === 'general' ? e.message : `${e.field}: ${e.message}`),
        }))
        .filter(lead => errorRowNumbers.has(lead.originalRow));
      setEditableLeads(updated);
      
      // Reset to first page if current page is now out of bounds
      const newTotalPages = Math.ceil(updated.length / rowsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        setCurrentPage(newTotalPages);
      }
    }
  }, [validationErrors, leads, initialErrorCount, currentPage, rowsPerPage, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div 
        className="bg-slate-900 border border-white/20 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Fix CSV Errors</h3>
              <p className="text-sm text-gray-400 mt-1">
                {editableLeads.length} {editableLeads.length === 1 ? 'row' : 'rows'} with errors
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          {successMessage && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-green-500/10 border border-green-500/30 rounded-lg">
              <CheckIcon className="h-4 w-4 text-green-400 flex-shrink-0" />
              <span className="text-sm text-green-400">{successMessage}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
          {/* Pagination Controls - Top */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
              <div className="text-sm text-gray-400">
                Showing {startIndex + 1}-{Math.min(endIndex, editableLeads.length)} of {editableLeads.length} rows
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <span className="text-sm text-gray-300 min-w-[80px] text-center">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-900 z-10">
                <tr className="border-b border-white/10">
                  <th className="text-left text-xs font-medium text-gray-400 pb-3 pr-4">Row</th>
                  <th className="text-left text-xs font-medium text-gray-400 pb-3 pr-4 min-w-[150px]">Name *</th>
                  <th className="text-left text-xs font-medium text-gray-400 pb-3 pr-4 min-w-[180px]">Phone *</th>
                  <th className="text-left text-xs font-medium text-gray-400 pb-3 min-w-[200px]">Email</th>
                </tr>
              </thead>
              <tbody>
                {currentPageLeads.map((lead, pageIndex) => {
                  const actualIndex = startIndex + pageIndex;
                  return (
                    <React.Fragment key={actualIndex}>
                      <tr className="border-b border-white/5">
                        <td className="py-3 pr-4 text-gray-400 align-top font-mono text-xs">{lead.originalRow}</td>
                        <td className="py-3 pr-4">
                          <input
                            type="text"
                            value={lead.name}
                            onChange={(e) => handleFieldChange(actualIndex, 'name', e.target.value)}
                            className="w-full px-3 py-2 bg-slate-950/60 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                            placeholder="Enter name"
                          />
                        </td>
                        <td className="py-3 pr-4">
                          <input
                            type="text"
                            value={lead.phone}
                            onChange={(e) => handleFieldChange(actualIndex, 'phone', e.target.value)}
                            className="w-full px-3 py-2 bg-slate-950/60 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                            placeholder={PHONE_PLACEHOLDER_MASK}
                          />
                        </td>
                        <td className="py-3">
                          <input
                            type="text"
                            value={lead.email}
                            onChange={(e) => handleFieldChange(actualIndex, 'email', e.target.value)}
                            className="w-full px-3 py-2 bg-slate-950/60 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                            placeholder="email@example.com"
                          />
                        </td>
                      </tr>
                      {lead.errors.length > 0 && (
                        <tr>
                          <td></td>
                          <td colSpan={3} className="pb-4">
                            <div className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded p-2 space-y-1">
                              {lead.errors.map((error, errIdx) => (
                                <div key={errIdx}>• {error}</div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-white/10 bg-slate-900/50">
          <p className="text-xs text-gray-400">
            Make your corrections and click "Save & Validate" to check if errors are fixed.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-300 bg-white/5 border border-white/20 rounded hover:bg-white/10 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-cyan-500 to-blue-600 rounded hover:from-cyan-600 hover:to-blue-700 transition-colors shadow-lg"
            >
              <CheckIcon className="h-4 w-4" />
              Save & Validate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorEditorModal;
